// @flow

import * as React from "react";

// $0 stays "$0", but $149.90 must not render as "$149.9" (and $149.90 / 12 shows as $12.49)
export function formatPrice(price: number | string): string {
    const n = Number(price);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// Growth (monthly and yearly) gets the "Recommended" ribbon. Matched by name because the Paddle plan ids differ
// between the sandbox and live.
export function isRecommendedPlan(plan: {plan_name?: string}): boolean {
    return /^growth\b/i.test((plan.plan_name || "").trim());
}

// Every paid plan has every feature; only the monthly QR invoice allowance differs. (The Free plan isn't shown as a card -
// Usage & Plan lists it on its own line without features - and it can't email QR invoices.) Kept short so each fits on one
// line in the narrowest card; the key words are highlighted (.plan-card-key).
const Key = ({children}) => <span className="plan-card-key">{children}</span>;
const PLAN_FEATURES = [
    ["sync", <React.Fragment><Key>Automatic</Key> QuickBooks Connection</React.Fragment>],
    ["drag", <React.Fragment><Key>Drag & drop</Key> QR placement</React.Fragment>],
    ["links", <React.Fragment><Key>Payment link</Key> QR codes</React.Fragment>],
    ["email", <React.Fragment><Key>Email QR Invoices</Key> to customers</React.Fragment>],
    ["fatoora", <React.Fragment><Key>ZATCA FATOORA</Key> Integration</React.Fragment>],
    ["team", <React.Fragment><Key>Team</Key> & <Key>multi-company</Key></React.Fragment>],
    ["support", <React.Fragment><Key>Online support</Key></React.Fragment>],
];
const PLAN_FEATURES_COMING_SOON = [
    ["auto", <React.Fragment><Key>Automatic</Key> QR invoice generation</React.Fragment>],
];

type Props = {|
  +plan: {id: number, plan_name: string, plan_price: number, max_qr_invoices: number, billing_type: number},
  +button: React.Node,
|};

// One paid plan card (Usage & Plan page, the legacy-plan modal and PremiumFeatureModal). The caller supplies the button.
function PlanCard({plan, button}: Props): React.Node {
    // A yearly plan shows its monthly equivalent (a twelfth of the yearly price, so $149.90 a year shows $12.49),
    // labelled "Billed yearly". The confirmation before a change still states the full amount that is charged.
    const yearly = plan.billing_type === 2;
    return (
        <div className="app-card plan-card">
            {isRecommendedPlan(plan) ? <div className="plan-card-ribbon">Recommended</div> : null}
            <div className="plan-card-name" style={{fontWeight: 700, fontSize: "1.1rem"}}>{plan.plan_name}</div>
            <div className="plan-card-price" style={{fontSize: "1.8rem", fontWeight: 700, marginTop: "12px"}}>
                ${formatPrice(yearly ? Number(plan.plan_price) / 12 : plan.plan_price)}<span style={{fontSize: "14px", fontWeight: 400}}>/month</span>
            </div>
            <div className="plan-card-billing">{yearly ? "Billed yearly" : "Billed monthly"}</div>
            <div className="plan-card-quota">
                Generate up to <b>{plan.max_qr_invoices} QR Invoices</b> every month
            </div>
            <ul className="plan-card-features">
                {PLAN_FEATURES.map(([key, feature]) => <li key={key}>✓ {feature}</li>)}
                <li className="plan-card-features-heading">Coming soon</li>
                {PLAN_FEATURES_COMING_SOON.map(([key, feature]) => <li key={key}>✓ {feature}</li>)}
            </ul>
            {button}
        </div>
    );
}

export default PlanCard;
